/**
 * Metrics Builder - Transforms SIE data into MetricSeries for analysis
 * 
 * This module builds time series from existing aggregated SIE data.
 * No new data processing - only transforms what's already computed.
 */

import type { MetricSeries, TopDriver } from './types';
import type { SIEProcessedData, MonthlyIncomeData } from '../types';

/**
 * Build all metrics from SIE data
 */
export function buildMetrics(
  currentYear: SIEProcessedData,
  previousYear?: SIEProcessedData
): MetricSeries[] {
  const metrics: MetricSeries[] = [];
  
  const currByMonth = currentYear.aggregated.incomeStatementByMonth || {};
  const prevByMonth = previousYear?.aggregated.incomeStatementByMonth || {};
  const currByAccountAndMonth = currentYear.aggregated.incomeStatementByAccountAndMonth || {};
  const prevByAccountAndMonth = previousYear?.aggregated.incomeStatementByAccountAndMonth || {};
  
  // Sort periods chronologically
  const periods = Object.keys(currByMonth).sort();
  const prevPeriods = Object.keys(prevByMonth).sort();
  
  if (periods.length === 0) {
    return metrics;
  }
  
  // 1. Aggregate metrics (key financials)
  metrics.push(
    buildAggregateMetric('REVENUE_TOTAL', 'Totala intäkter', periods, currByMonth, prevPeriods, prevByMonth, 'revenue'),
    buildAggregateMetric('EXPENSES_TOTAL', 'Totala kostnader', periods, currByMonth, prevPeriods, prevByMonth, 'expenses'),
    buildAggregateMetric('OPERATING_RESULT', 'Rörelseresultat', periods, currByMonth, prevPeriods, prevByMonth, 'operatingResult'),
    buildAggregateMetric('FINANCIAL_NET', 'Finansnetto', periods, currByMonth, prevPeriods, prevByMonth, 'financialNet'),
    buildAggregateMetric('NET_RESULT', 'Nettoresultat', periods, currByMonth, prevPeriods, prevByMonth, 'netResult'),
  );
  
  // 2. Per-account metrics for significant accounts
  const significantAccounts = findSignificantAccounts(currByAccountAndMonth, periods);
  for (const accountInfo of significantAccounts) {
    const accountMetric = buildAccountMetric(
      accountInfo.account,
      accountInfo.name,
      periods,
      currByAccountAndMonth,
      prevPeriods,
      prevByAccountAndMonth
    );
    if (accountMetric) {
      metrics.push(accountMetric);
    }
  }
  
  return metrics;
}

/**
 * Build aggregate metric from monthly income data
 */
function buildAggregateMetric(
  key: string,
  name: string,
  periods: string[],
  currByMonth: Record<string, MonthlyIncomeData>,
  prevPeriods: string[],
  prevByMonth: Record<string, MonthlyIncomeData>,
  field: keyof MonthlyIncomeData
): MetricSeries {
  const values = periods.map(p => currByMonth[p]?.[field] ?? 0);
  
  // Map current periods to previous year's corresponding months
  const comparativeValues = periods.map(p => {
    const prevPeriod = findPreviousYearPeriod(p, prevPeriods);
    return prevPeriod ? (prevByMonth[prevPeriod]?.[field] ?? 0) : 0;
  });
  
  return {
    metric_key: key,
    display_name: name,
    periods,
    values,
    comparative_values: comparativeValues.some(v => v !== 0) ? comparativeValues : undefined,
    meta: {
      accounts_included: [],
    },
  };
}

/**
 * Find significant accounts by total turnover
 */
function findSignificantAccounts(
  byAccountAndMonth: Record<string, Record<string, number>>,
  periods: string[]
): { account: string; name: string; total: number }[] {
  const totals = Object.entries(byAccountAndMonth).map(([account, months]) => ({
    account,
    name: account, // We'll get proper name from accounts list if available
    total: Object.values(months).reduce((sum, v) => sum + Math.abs(v), 0),
  }));
  
  // Return top 15 accounts by total turnover
  return totals
    .sort((a, b) => b.total - a.total)
    .slice(0, 15);
}

/**
 * Build metric for a single account
 */
function buildAccountMetric(
  account: string,
  name: string,
  periods: string[],
  currByAccountAndMonth: Record<string, Record<string, number>>,
  prevPeriods: string[],
  prevByAccountAndMonth: Record<string, Record<string, number>>
): MetricSeries | null {
  const accountData = currByAccountAndMonth[account];
  if (!accountData) return null;
  
  const values = periods.map(p => accountData[p] ?? 0);
  
  // Get previous year data if available
  const prevAccountData = prevByAccountAndMonth[account];
  const comparativeValues = prevAccountData
    ? periods.map(p => {
        const prevPeriod = findPreviousYearPeriod(p, prevPeriods);
        return prevPeriod ? (prevAccountData[prevPeriod] ?? 0) : 0;
      })
    : undefined;
  
  return {
    metric_key: `ACCOUNT_${account}`,
    display_name: `Konto ${account}`,
    periods,
    values,
    comparative_values: comparativeValues?.some(v => v !== 0) ? comparativeValues : undefined,
    meta: {
      accounts_included: [account],
    },
  };
}

/**
 * Find corresponding period from previous year
 * E.g., 2024-03 -> 2023-03
 */
function findPreviousYearPeriod(currentPeriod: string, prevPeriods: string[]): string | undefined {
  const [year, month] = currentPeriod.split('-');
  const prevYear = String(parseInt(year, 10) - 1);
  const targetPeriod = `${prevYear}-${month}`;
  
  return prevPeriods.includes(targetPeriod) ? targetPeriod : undefined;
}

/**
 * Calculate top drivers (accounts contributing most) per period
 */
export function calculateTopDrivers(
  byAccountAndMonth: Record<string, Record<string, number>>,
  accounts?: { number: string; name: string }[]
): Record<string, TopDriver[]> {
  const drivers: Record<string, TopDriver[]> = {};
  const accountNames = new Map(accounts?.map(a => [a.number, a.name]) || []);
  
  // Get all periods
  const allPeriods = new Set<string>();
  Object.values(byAccountAndMonth).forEach(months => {
    Object.keys(months).forEach(p => allPeriods.add(p));
  });
  
  // For each period, find top 5 accounts by absolute amount
  for (const period of allPeriods) {
    const periodAmounts: { account: string; name: string; amount: number }[] = [];
    
    for (const [account, months] of Object.entries(byAccountAndMonth)) {
      const amount = months[period];
      if (amount !== undefined && amount !== 0) {
        periodAmounts.push({
          account,
          name: accountNames.get(account) || account,
          amount,
        });
      }
    }
    
    // Sort by absolute amount and take top 5
    drivers[period] = periodAmounts
      .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
      .slice(0, 5);
  }
  
  return drivers;
}
